import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignIn } from "@clerk/react";
import { dark } from "@clerk/ui/themes";
import { toast } from "sonner";
import { BrandLogo } from "../components/brand/BrandLogo";
import { useApi, errMsg } from "../lib/api";

const KINDS = [
  { id: "individual", mark: "Me", name: "Individual", note: "My own profile & family" },
  { id: "org", mark: "Org", name: "Organisation", note: "School, company, team" },
  { id: "station", mark: "NFC", name: "Reader station", note: "Gate or front desk" },
  { id: "staff", mark: "bs", name: "bsafe staff", note: "Platform & print team" },
];
const KEY = "bsafe_signin_as";

export default function SignInPage() {
  const [kind, setKind] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY))?.kind || "individual"; } catch { return "individual"; } });
  const [ws, setWs] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY))?.ws || ""; } catch { return ""; } });
  const [station, setStation] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY))?.station || ""; } catch { return ""; } });
  const needsWs = kind === "org" || kind === "station";
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify({ kind, ws: ws.trim().toLowerCase(), station: station.trim() })); }, [kind, ws, station]);
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B1530] text-white" style={{ fontFamily: "Sora, sans-serif" }} data-testid="auth-page-sign-in">
      <aside className="relative overflow-hidden p-8 lg:p-14 flex flex-col justify-between min-h-[260px]">
        <Link to="/" data-testid="auth-logo"><BrandLogo tone="dark" size={40} /></Link>
        <div className="relative z-10 mt-10 lg:mt-0">
          <div className="text-xs font-bold tracking-[0.35em] text-[#FF7A8A]">EVERY SECOND COUNTS</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold">Sign in to bsafe</h1>
          <p className="mt-4 text-slate-300 max-w-md">Every organisation, staff member and individual has their own login. You only see what your account is allowed to see, and every sign-in is recorded.</p>
        </div>
        <div className="relative z-10 text-xs text-slate-500 mt-10">A product of <b>MUGAWE INVESTMENTS U LTD</b></div>
        {[260, 340, 420, 500].map((s) => <span key={s} className="absolute rounded-full border border-slate-700/50 pointer-events-none" style={{ width: s, height: s, bottom: -s / 2.4, right: -s / 4 }} />)}
      </aside>
      <main className="bg-[#0F1B3D] flex flex-col items-center justify-center p-5 sm:p-10">
        <div className="w-full max-w-[440px]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="signin-kind-picker">{KINDS.map((k) => (
            <button key={k.id} onClick={() => setKind(k.id)} data-testid={`signin-kind-${k.id}`}
              className={`rounded-2xl p-3 text-left border transition-colors ${kind === k.id ? "border-[#D7263D] bg-[#D7263D]/15" : "border-slate-700 hover:border-slate-500"}`}>
              <span className={`inline-flex w-8 h-8 rounded-lg items-center justify-center text-[11px] font-black ${kind === k.id ? "bg-[#D7263D]" : "bg-slate-700"}`}>{k.mark}</span>
              <div className="mt-2 text-sm font-bold">{k.name}</div><div className="text-[10px] text-slate-400 leading-tight">{k.note}</div></button>))}</div>
          {needsWs && <div className="mt-5 space-y-3">
            <label className="block text-sm font-semibold">Your organisation's workspace
              <div className="mt-1.5 flex items-center rounded-xl bg-[#16294A] border border-slate-700 px-3 h-12"><span className="text-slate-400 text-sm">bsafe.today/</span>
                <input data-testid="signin-workspace-input" value={ws} onChange={(e) => setWs(e.target.value)} placeholder="your-organisation" className="flex-1 bg-transparent outline-none text-sm ml-1" /></div>
              <span className="text-xs text-slate-400 font-normal">Each group has its own sign-in page and only sees its own people.</span></label>
            {kind === "station" && <label className="block text-sm font-semibold">Station name
              <input data-testid="signin-station-input" value={station} onChange={(e) => setStation(e.target.value)} placeholder="e.g. MAIN-GATE-01" className="mt-1.5 w-full rounded-xl bg-[#16294A] border border-slate-700 px-3 h-12 text-sm outline-none" /></label>}
          </div>}
          <div className="mt-5" data-testid="clerk-signin">
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp forceRedirectUrl="/app/go"
              appearance={{ theme: dark, variables: { colorPrimary: "#D7263D", colorBackground: "#16294A" } }} />
          </div>
          <p className="mt-5 text-sm text-slate-400">New to bsafe? <Link to="/app/onboarding" className="text-[#FF7A8A] underline">Create a group</Link> or <Link to="/sign-up" className="text-[#FF7A8A] underline" data-testid="signin-signup-link">get your own free profile</Link>.</p>
          <Link to="/" className="mt-4 inline-block text-xs text-slate-500 hover:text-white" data-testid="auth-back-home">← Back to bsafe.today</Link>
        </div>
      </main>
    </div>
  );
}

export function GoRedirect() {
  const api = useApi();
  const nav = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let pick = {};
    try { pick = JSON.parse(localStorage.getItem(KEY)) || {}; } catch { pick = {}; }
    const done = (to) => { localStorage.removeItem(KEY); nav(to, { replace: true }); };
    if (pick.kind === "staff") { done("/app/admin"); return; }
    if ((pick.kind === "org" || pick.kind === "station") && pick.ws) {
      api.get(`/workspace/${encodeURIComponent(pick.ws)}`).then((r) => {
        if (pick.kind === "station") { localStorage.setItem("bsafe_station", pick.station || ""); done(`/reader/${r.data.id}`); }
        else done(r.data.my_role === "nurse" ? `/app/g/${r.data.id}/nurse` : `/app/g/${r.data.id}`);
      }).catch((e) => { if (e?.response?.status === 401) return; toast.error(errMsg(e)); done("/app"); });
      return;
    }
    done("/app");
  }, [api, nav]);
  return <div className="text-slate-400" data-testid="signin-redirecting">Opening your workspace…</div>;
}
